/*  v3.11 · Fuel Surcharge settings card
 *  ---------------------------------------------------------
 *  Lives on /settings. Accounts staff types today's diesel TGP each
 *  morning (5 seconds) OR lets the 5:30 AM cron auto-pull it. Three
 *  calculation modes are supported: Binary, Proportional, and Ratio
 *  per m³ (auto-scales the $/m³ surcharge with TGP).
 * ------------------------------------------------------- */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Fuel, RefreshCcw, Save, AlertTriangle, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export default function FuelSurchargeCard() {
  const [settings, setSettings] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/settings/fuel-surcharge");
      setSettings(r.data);
      setPriceInput(String(r.data.current_price || ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load fuel surcharge settings");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePrice = async () => {
    const p = parseFloat(priceInput);
    if (!(p > 0)) { toast.error("Enter a positive $ per litre"); return; }
    setSaving(true);
    try {
      const r = await api.post("/settings/fuel-surcharge/set-price", { price: p });
      setSettings(r.data);
      const mode = (r.data.mode || "").toLowerCase();
      if (mode === "ratio_per_m3") {
        toast.success(`Diesel TGP $${p.toFixed(3)}/L · surcharge $${(r.data.current_surcharge_per_m3 || 0).toFixed(2)}/m³`, { duration: 5000 });
      } else {
        toast.success(`Diesel TGP $${p.toFixed(3)}/L · surcharge ${(r.data.current_surcharge_pct || 0).toFixed(2)}%`, { duration: 5000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update price");
    } finally { setSaving(false); }
  };

  const saveConfig = async (partial) => {
    setSaving(true);
    try {
      const r = await api.put("/settings/fuel-surcharge", partial);
      setSettings(r.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const fetchNow = async () => {
    setFetching(true);
    try {
      const r = await api.post("/settings/fuel-surcharge/fetch-now");
      setSettings(r.data);
      setPriceInput(String(r.data.current_price || ""));
      toast.success(`Fetched $${r.data.current_price.toFixed(3)}/L from ${r.data.fetched_from || "public source"}`, { duration: 6000 });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.hint : (detail || "Fetch failed — enter manually"), { duration: 7000 });
    } finally { setFetching(false); }
  };

  if (!settings) {
    return <div className="border border-industrial p-4 mono text-xs text-muted-industrial uppercase">Loading fuel surcharge settings…</div>;
  }

  const mode = (settings.mode || "binary").toLowerCase();
  const isRatio = mode === "ratio_per_m3";
  const pct = settings.current_surcharge_pct || 0;
  const perM3 = settings.current_surcharge_per_m3 || 0;
  const active = settings.enabled && (isRatio ? perM3 > 0 : pct > 0);
  const belowThreshold = !isRatio && settings.enabled && settings.current_price > 0 && settings.current_price <= settings.threshold_price;

  // effective_from banner
  let effBanner = null;
  if (settings.effective_from) {
    const eff = new Date(settings.effective_from + "T00:00:00");
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (eff > today) {
      effBanner = `Scheduled to activate ${eff.toLocaleDateString("en-AU", { weekday: "short", day: "2-digit", month: "short" })} — today's dockets are NOT surcharged yet.`;
    }
  }

  // Base ratio for ratio_per_m3 display
  const baseTgp = Number(settings.base_tgp || 0);
  const baseAmt = Number(settings.base_surcharge_per_m3 || 0);
  const baseRatio = baseTgp > 0 ? (baseAmt / baseTgp) : 0;

  return (
    <div className="border-2 border-industrial bg-surface" data-testid="fuel-surcharge-card">
      <div className="border-b border-industrial px-4 py-3 flex items-center gap-2">
        <Fuel className={`w-4 h-4 ${active ? "text-emerald-400" : "text-muted-industrial"}`} />
        <div className="heading uppercase tracking-widest text-sm">Fuel Surcharge · Account Invoices</div>
        <div className={`ml-auto mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${active ? "border-emerald-500 text-emerald-400" : "border-industrial text-muted-industrial"}`}>
          {active ? "ACTIVE" : "OFF"}
        </div>
      </div>

      {effBanner && (
        <div className="border-b border-amber-500 bg-amber-950/20 px-4 py-2 flex items-center gap-2" data-testid="fuel-effective-banner">
          <CalendarClock className="w-3.5 h-3.5 text-amber-400" />
          <div className="mono text-[11px] text-amber-300">{effBanner}</div>
        </div>
      )}

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT — Today's price */}
        <div className="space-y-3">
          <div>
            <Label className="label-tag">Today's Diesel TGP ($/L)</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                data-testid="fuel-price-input"
                type="number"
                step="0.001"
                min="0"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") savePrice(); }}
                placeholder="2.480"
                className="rounded-none bg-transparent border-industrial mono text-2xl font-black text-primary-orange"
              />
              <Button
                onClick={savePrice}
                disabled={saving}
                data-testid="fuel-price-save-btn"
                className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"
              >
                <Save className="w-3.5 h-3.5 mr-1" />Set
              </Button>
            </div>
            <div className="mono text-[10px] text-muted-industrial mt-1">
              Last updated: {settings.updated_at ? new Date(settings.updated_at).toLocaleString() : "never"} by {settings.updated_by || "—"} ({settings.source})
            </div>
            {settings.last_scrape_at && (
              <div className={`mono text-[10px] mt-0.5 ${settings.last_scrape_status === "ok" ? "text-emerald-400" : "text-amber-400"}`} data-testid="fuel-last-scrape">
                Morning auto-pull ({settings.last_scrape_status}): {new Date(settings.last_scrape_at).toLocaleString()}
                {settings.last_scrape_error ? ` · ${settings.last_scrape_error}` : ""}
              </div>
            )}
          </div>

          <Button
            variant="outline"
            onClick={fetchNow}
            disabled={fetching}
            data-testid="fuel-fetch-btn"
            className="rounded-none border-industrial hover:border-emerald-500 hover:text-emerald-400 uppercase tracking-widest text-xs w-full"
          >
            <RefreshCcw className={`w-3.5 h-3.5 mr-1 ${fetching ? "animate-spin" : ""}`} />
            {fetching ? "Fetching…" : "Auto-Fetch AIP Hobart Diesel"}
          </Button>

          {/* Live surcharge readout */}
          <div className={`border-2 p-3 ${active ? "border-emerald-500 bg-emerald-950/20" : "border-industrial bg-surface-2"}`} data-testid="fuel-surcharge-preview">
            <div className="label-tag mb-1 flex items-center justify-between">
              <span>Surcharge on today's invoices</span>
              <span className="text-[9px] opacity-70">{mode.toUpperCase().replace("_", " ")}</span>
            </div>
            {isRatio ? (
              <>
                <div className={`mono text-4xl font-black ${active ? "text-emerald-300" : "text-muted-industrial"}`} data-testid="fuel-surcharge-per-m3">
                  ${perM3.toFixed(2)}<span className="text-lg font-normal opacity-70">/m³</span>
                </div>
                {active && (
                  <div className="mono text-[10px] text-muted-industrial mt-1">
                    Formula: ${settings.current_price.toFixed(3)}/L × ratio {baseRatio.toFixed(4)} = <b className="text-emerald-300">${perM3.toFixed(4)}/m³</b>
                  </div>
                )}
                {active && (
                  <div className="mono text-[10px] text-muted-industrial">
                    Example: 8m³ load → <b className="text-emerald-300">+${(perM3 * 8).toFixed(2)}</b> fuel surcharge (ex-GST)
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={`mono text-4xl font-black ${active ? "text-emerald-300" : "text-muted-industrial"}`} data-testid="fuel-surcharge-pct">
                  {pct.toFixed(2)}%
                </div>
                {active && (
                  <div className="mono text-[10px] text-muted-industrial mt-1">
                    Example: $1,000 ex-GST → <b className="text-emerald-300">+${(1000 * pct / 100).toFixed(2)}</b> fuel surcharge · GST applies on the surcharged base
                  </div>
                )}
                {belowThreshold && (
                  <div className="mono text-[10px] text-muted-industrial mt-1">
                    Diesel ${settings.current_price.toFixed(3)}/L is at or below the ${settings.threshold_price.toFixed(2)}/L threshold — no surcharge added.
                  </div>
                )}
              </>
            )}
            {settings.enabled && settings.current_price === 0 && (
              <div className="mono text-[10px] text-amber-400 mt-1 flex items-start gap-1">
                <AlertTriangle className="w-3 h-3 mt-0.5" /> No diesel price entered yet — surcharge will stay at 0% until you set today's TGP.
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Configuration */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 mono text-xs uppercase tracking-widest cursor-pointer" data-testid="fuel-enabled-label">
            <Checkbox
              checked={!!settings.enabled}
              onCheckedChange={(v) => saveConfig({ enabled: !!v })}
              data-testid="fuel-enabled-cb"
            />
            <span className={settings.enabled ? "text-emerald-400" : "text-muted-industrial"}>
              {settings.enabled ? "Surcharge enabled" : "Surcharge disabled (globally off)"}
            </span>
          </label>

          <div>
            <Label className="label-tag mb-1 block">Calculation model</Label>
            <div className="grid grid-cols-3 gap-1" data-testid="fuel-mode-toggle">
              <button
                type="button"
                onClick={() => saveConfig({ mode: "binary" })}
                data-testid="fuel-mode-binary"
                className={`p-2 border text-left ${mode === "binary" ? "border-emerald-500 bg-emerald-950/20 text-emerald-300" : "border-industrial text-muted-industrial hover:text-ink"}`}
              >
                <div className="mono text-[10px] uppercase tracking-widest font-bold">Binary</div>
                <div className="mono text-[9px] opacity-80 mt-0.5">Flat {settings.rate_percent}% when diesel {'>'} ${settings.threshold_price.toFixed(2)}.</div>
              </button>
              <button
                type="button"
                onClick={() => saveConfig({ mode: "proportional" })}
                data-testid="fuel-mode-proportional"
                className={`p-2 border text-left ${mode === "proportional" ? "border-emerald-500 bg-emerald-950/20 text-emerald-300" : "border-industrial text-muted-industrial hover:text-ink"}`}
              >
                <div className="mono text-[10px] uppercase tracking-widest font-bold">Proportional</div>
                <div className="mono text-[9px] opacity-80 mt-0.5">{settings.rate_percent}% × (TGP − ${settings.threshold_price.toFixed(2)}) / ${settings.threshold_price.toFixed(2)}.</div>
              </button>
              <button
                type="button"
                onClick={() => saveConfig({ mode: "ratio_per_m3" })}
                data-testid="fuel-mode-ratio-per-m3"
                className={`p-2 border text-left ${mode === "ratio_per_m3" ? "border-emerald-500 bg-emerald-950/20 text-emerald-300" : "border-industrial text-muted-industrial hover:text-ink"}`}
              >
                <div className="mono text-[10px] uppercase tracking-widest font-bold">Ratio $/m³</div>
                <div className="mono text-[9px] opacity-80 mt-0.5">Base ${baseAmt.toFixed(2)}/m³ @ ${baseTgp.toFixed(2)}/L auto-scales with today's TGP.</div>
              </button>
            </div>
          </div>

          {isRatio ? (
            <div className="grid grid-cols-2 gap-2" data-testid="fuel-ratio-config">
              <div>
                <Label className="label-tag">Base TGP ($/L)</Label>
                <Input
                  data-testid="fuel-base-tgp-input"
                  type="number" step="0.001" min="0"
                  defaultValue={baseTgp}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (v > 0 && v !== baseTgp) saveConfig({ base_tgp: v });
                  }}
                  className="rounded-none bg-transparent border-industrial mono"
                />
              </div>
              <div>
                <Label className="label-tag">Base Surcharge ($/m³)</Label>
                <Input
                  data-testid="fuel-base-per-m3-input"
                  type="number" step="0.01" min="0"
                  defaultValue={baseAmt}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (v > 0 && v !== baseAmt) saveConfig({ base_surcharge_per_m3: v });
                  }}
                  className="rounded-none bg-transparent border-industrial mono"
                />
              </div>
              <div className="col-span-2 mono text-[10px] text-muted-industrial">
                Ratio locked in: <b className="text-emerald-300">{baseRatio.toFixed(4)}</b> (= base $/m³ ÷ base $/L). Any TGP moves scale the per-m³ figure proportionally.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="label-tag">Threshold ($/L)</Label>
                <Input
                  data-testid="fuel-threshold-input"
                  type="number" step="0.01" min="0"
                  defaultValue={settings.threshold_price}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (v > 0 && v !== settings.threshold_price) saveConfig({ threshold_price: v });
                  }}
                  className="rounded-none bg-transparent border-industrial mono"
                />
              </div>
              <div>
                <Label className="label-tag">Rate (%)</Label>
                <Input
                  data-testid="fuel-rate-input"
                  type="number" step="0.01" min="0"
                  defaultValue={settings.rate_percent}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (v > 0 && v !== settings.rate_percent) saveConfig({ rate_percent: v });
                  }}
                  className="rounded-none bg-transparent border-industrial mono"
                />
              </div>
            </div>
          )}

          <div>
            <Label className="label-tag">Effective From (blank = active immediately)</Label>
            <Input
              data-testid="fuel-effective-from-input"
              type="date"
              defaultValue={settings.effective_from || ""}
              onBlur={(e) => {
                const v = (e.target.value || "").trim();
                if (v !== (settings.effective_from || "")) saveConfig({ effective_from: v });
              }}
              className="rounded-none bg-transparent border-industrial mono"
            />
          </div>

          <div className="text-[10px] mono text-muted-industrial border-t border-industrial pt-2">
            <div className="mb-1"><b className="text-ink">Auto-pull:</b> 5:30 AM plant-local (Australia/Hobart) via AIP Hobart Diesel page.</div>
            <div>· Account customers only (CASH always exempt). Per-customer exemption on customer card.</div>
            <div>· Applied at invoice-level (one FUEL SURCHARGE line above GST); the surcharge itself is taxable.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
